import React, { useState, useEffect } from 'react';
import { useSubmitHealthyPledgeMutation } from '../../redux/auth/authApi';
import axiosConn from '../../config/axiosConn';
import { toast } from 'react-toastify';
import { FaUser, FaUsers, FaTimes, FaArrowDown, FaMapMarkerAlt } from 'react-icons/fa';
import { processGeotaggedImage } from './geotaggedUtils';
import { getGeoFailureMessage } from './gpsFallbackUtils';
import './PledgeModal.css';

// Logo Imports
import samagraLogo from '../../assets/images/samagra-logo.png';
import ecoClubLogo from '../../assets/images/Logo-2.png';
import moeLogo from '../../assets/images/schoolEdu-logo.png';
import pmShriLogo from '../../assets/images/pmshree-logo.png';

const PledgeModal = ({ isOpen, initialData, onClose }) => {
    const [hasTakenPledge, setHasTakenPledge] = useState(null);
    const [teachersCount, setTeachersCount] = useState('');
    const [studentsCount, setStudentsCount] = useState('');
    const [teachersError, setTeachersError] = useState('');
    const [studentsError, setStudentsError] = useState('');
    const [pledgeImageUrl, setPledgeImageUrl] = useState('');
    const [latitude, setLatitude] = useState(null);
    const [longitude, setLongitude] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [imageError, setImageError] = useState('');
    const [submitPledge, { isLoading }] = useSubmitHealthyPledgeMutation();

    useEffect(() => {
        if (initialData) {
            setHasTakenPledge(initialData.hasTakenPledge);
            setTeachersCount(initialData.teachersCount?.toString() || '');
            setStudentsCount(initialData.studentsCount?.toString() || '');
            setPledgeImageUrl(initialData.pledgeImageUrl || '');
            setLatitude(initialData.latitude || null);
            setLongitude(initialData.longitude || null);
        }
    }, [initialData]);

    if (!isOpen) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (hasTakenPledge === null) {
            toast.error("Please select an option");
            return;
        }

        let hasError = false;

        if (!teachersCount) {
            setTeachersError("This field is required");
            hasError = true;
        } else if (parseInt(teachersCount) > 5000) {
            setTeachersError("Maximum allowed limit is 5000");
            hasError = true;
        } else if (parseInt(teachersCount) < 1) {
            setTeachersError("Minimum value should be 1");
            hasError = true;
        }

        if (!studentsCount) {
            setStudentsError("This field is required");
            hasError = true;
        } else if (parseInt(studentsCount) > 5000) {
            setStudentsError("Maximum allowed limit is 5000");
            hasError = true;
        } else if (parseInt(studentsCount) < 1) {
            setStudentsError("Minimum value should be 1");
            hasError = true;
        }

        if (uploading) {
            toast.info("Please wait for image to upload");
            return;
        }

        if (hasError) return;

        const tCount = parseInt(teachersCount);
        const sCount = parseInt(studentsCount);

        try {
            await submitPledge({
                hasTakenPledge: true,
                teachersCount: tCount,
                studentsCount: sCount,
                pledgeImageUrl: pledgeImageUrl,
                latitude: latitude,
                longitude: longitude
            }).unwrap();
            onClose();
        } catch (err) {
            console.error("Failed to submit pledge:", err);
        }
    };

    const handleNumberInput = (e, setter, errorSetter) => {
        const rawValue = e.target.value;
        const cleanedValue = rawValue.replace(/[^0-9]/g, '');

        if (cleanedValue === '') {
            setter('');
            errorSetter('');
            return;
        }

        const numVal = parseInt(cleanedValue, 10);

        if (numVal > 5000) {
            setter(cleanedValue.slice(0, 4));
            errorSetter('Maximum allowed limit is 5000');
            return;
        }

        if (numVal === 0) {
            setter(cleanedValue);
            errorSetter('Minimum value should be 1');
            return;
        }

        errorSetter('');
        setter(numVal.toString());
    };

    const handlePaste = (e, errorSetter) => {
        const pasteData = e.clipboardData.getData('text');
        const cleanedPaste = pasteData.replace(/[^0-9]/g, '');
        if (cleanedPaste !== pasteData) {
            e.preventDefault();
            errorSetter('Only numeric values are allowed');
        }
    };

    const handleBlur = (val, errorSetter) => {
        if (!val) {
            errorSetter("This field is required");
        } else if (parseInt(val) < 1) {
            errorSetter("Minimum value should be 1");
        }
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validation
        const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp'];
        if (!allowedTypes.includes(file.mimetype || file.type)) {
            setImageError("Only image files (JPEG, PNG, WEBP) are allowed");
            return;
        }

        setImageError("");
        setUploading(true);

        try {
            // Validate the photo's geo-tag; when it has none, the school's
            // current location is stamped into it instead of rejecting it.
            const processedData = await processGeotaggedImage(file, 1, {
                fallbackToDeviceLocation: true,
                section: 'Pledge',
            });
            const { processedFile, latitude: lat, longitude: lng, geoTagAdded } = processedData;

            if (geoTagAdded) {
                toast.info("This photo was not geo-tagged, so your current location has been added to it.");
            }

            const formData = new FormData();
            formData.append('files', processedFile);

            // Using existing axiosConn setup - FormData handles its own headers
            const response = await axiosConn('post', 'upload-image-file', formData, null, null);

            if (response.data && response.data.status === "success") {
                const url = response.data.data[0]; // The API returns an array of URLs
                setPledgeImageUrl(url);
                setLatitude(lat);
                setLongitude(lng);
                toast.success("Geotagged image uploaded successfully!");
                setImageError(""); // Clear any previous error
            } else {
                const errorMsg = response.data?.message || "Failed to upload image. Please try again.";
                setImageError(errorMsg);
                toast.error(errorMsg);
            }
        } catch (err) {
            console.error("Image upload error detail:", err);

            // The photo had no GPS EXIF and the device location could not be
            // read either — walk the user through fixing the permission.
            if (err.reason) {
                const geoMessage = await getGeoFailureMessage(err);
                setImageError(geoMessage);
                toast.warning(geoMessage);
                return;
            }

            let errorMessage = "Check your connection.";
            if (err.response) {
                // Server responded with a status code outside the 2xx range
                errorMessage = err.response.data?.message || `Server error: ${err.response.status}`;
            } else if (err.request) {
                // Request was made but no response was received
                errorMessage = "No response from server. Is the backend running?";
            } else {
                // Something else happened while setting up the request
                errorMessage = err.message;
            }

            setImageError(`Upload failed: ${errorMessage}`);
            toast.error(`Upload failed: ${errorMessage}`);
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="pledge-modal-overlay">
            <div className="pledge-modal-container premium-eco-modal">
                <button className="modal-close-btn" onClick={onClose} aria-label="Close">
                    <FaTimes />
                </button>

                <div className="modal-bg-watercolor top"></div>

                <div className="pledge-modal-header">
                    <h1 className="eco-title">Environment Day 2026</h1>
                    <p className="eco-subtitle">Have You Taken the Pledge?</p>
                </div>

                <div className="pledge-modal-body">
                    <form onSubmit={handleSubmit}>
                        <div className="pledge-selection-hint">
                            <span>click here to select</span>
                            <span className="bounce-arrow"><FaArrowDown /></span>
                        </div>
                        <div className="pledge-pill-toggle">
                            <button
                                type="button"
                                className={`pill-btn ${hasTakenPledge === true ? 'active' : ''}`}
                                onClick={() => setHasTakenPledge(true)}
                            >
                                Yes
                            </button>
                            <button
                                type="button"
                                className={`pill-btn ${hasTakenPledge === false ? 'active' : ''}`}
                                onClick={() => setHasTakenPledge(false)}
                            >
                                No
                            </button>
                        </div>

                        {(hasTakenPledge !== null) && (
                            <div className="pledge-form-content">
                                {hasTakenPledge === false && (
                                    <div className="eco-pdf-section">
                                        <p className="eco-review-msg">Please review the pledge below before proceeding:</p>
                                        <div className="eco-pledge-document">
                                            <div className="pledge-doc-header">
                                                <img src={ecoClubLogo} alt="Eco Club" className="pledge-logo" />
                                                <img src={moeLogo} alt="MOE" className="pledge-logo" />
                                                <img src={samagraLogo} alt="Samagra" className="pledge-logo" />
                                                <img src={pmShriLogo} alt="PM Shri" className="pledge-logo" />
                                            </div>
                                            <div className="pledge-doc-content">

                                                <div className="pledge-text-english">
                                                    <p>
                                                        I pledge to make all possible changes in my daily life to protect the environment.
                                                        I also commit to continuously motivate my family, friends and others about the
                                                        importance of environmentally friendly habits.
                                                    </p>
                                                </div>

                                                <div className="pledge-text-hindi">
                                                    <p>
                                                        मैं प्रतिज्ञा करता/करती हूँ कि पर्यावरण को बचाने के लिए अपनी दैनिक जीवन में हर
                                                        संभव बदलाव लाऊंगा/लाऊंगी। मैं यह भी वचन देता/देती हूँ कि अपने परिवार, मित्रों
                                                        और अन्य लोगों को पर्यावरण के अनुकूल आदतों और व्यवहारों के महत्व के विषय में
                                                        सतत रूप से प्रेरित करूंगा/करूंगी।
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                <div className="eco-input-group">
                                    <label>Number of Teachers Participated</label>
                                    <div className={`input-wrapper ${teachersError ? 'has-error' : ''}`}>
                                        <input
                                            type="number"
                                            inputMode="numeric"
                                            pattern="[0-9]*"
                                            min="1"
                                            max="5000"
                                            value={teachersCount}
                                            onChange={(e) => handleNumberInput(e, setTeachersCount, setTeachersError)}
                                            onPaste={(e) => handlePaste(e, setTeachersError)}
                                            onBlur={() => handleBlur(teachersCount, setTeachersError)}
                                            placeholder="eg. 100"
                                            className={teachersError ? 'error-input' : ''}
                                        />
                                        <span className="input-icon"><FaUser /></span>
                                    </div>
                                    {teachersError && (
                                        <div className="validation-error">
                                            <span className="error-text">{teachersError}</span>
                                        </div>
                                    )}
                                </div>

                                <div className="eco-input-group">
                                    <label>Number of Students Participated</label>
                                    <div className={`input-wrapper ${studentsError ? 'has-error' : ''}`}>
                                        <input
                                            type="number"
                                            inputMode="numeric"
                                            pattern="[0-9]*"
                                            min="1"
                                            max="5000"
                                            value={studentsCount}
                                            onChange={(e) => handleNumberInput(e, setStudentsCount, setStudentsError)}
                                            onPaste={(e) => handlePaste(e, setStudentsError)}
                                            onBlur={() => handleBlur(studentsCount, setStudentsError)}
                                            placeholder="eg. 100"
                                            className={studentsError ? 'error-input' : ''}
                                        />
                                        <span className="input-icon"><FaUsers /></span>
                                    </div>
                                    {studentsError && (
                                        <div className="validation-error">
                                            <span className="error-text">{studentsError}</span>
                                        </div>
                                    )}
                                </div>

                                <div className="eco-input-group">
                                    <label>Upload Pledge Ceremony Photo (Optional)</label>
                                    <p className="geotag-hint">
                                        <FaMapMarkerAlt /> If the photo is not already geo-tagged, your current location will be added to it — please allow location access when your browser asks.
                                    </p>
                                    <div className={`input-wrapper image-upload-wrapper ${imageError ? 'has-error' : ''} ${pledgeImageUrl ? 'upload-success' : ''}`}>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={handleImageUpload}
                                            disabled={uploading}
                                            className="image-file-input"
                                        />
                                        {uploading && (
                                            <div className="upload-loader">
                                                <div className="spinner-border spinner-border-sm text-success" role="status"></div>
                                                <span className="ms-2">Uploading...</span>
                                            </div>
                                        )}
                                        {pledgeImageUrl && !uploading && (
                                            <div className="upload-preview-tag">
                                                <span className="text-success">✓ Image Uploaded</span>
                                            </div>
                                        )}
                                    </div>
                                    {imageError && (
                                        <div className="validation-error">
                                            <span className="error-text">{imageError}</span>
                                        </div>
                                    )}
                                    {pledgeImageUrl && (
                                        <div className="mt-2 text-center">
                                            <div className="position-relative d-inline-block">
                                                <img
                                                    src={pledgeImageUrl}
                                                    alt="Pledge Preview"
                                                    style={{ maxWidth: '100px', borderRadius: '8px', border: '2px solid #28a745', display: 'block' }}
                                                />
                                                {latitude && longitude && (
                                                    <div className="geotag-badge">
                                                        <FaMapMarkerAlt /> Geotagged
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <button
                                    type="submit"
                                    className="eco-submit-btn"
                                    disabled={isLoading || uploading}
                                >
                                    {isLoading ? 'Submitting...' : uploading ? 'Uploading Photo...' : 'Submit Pledge Details'}
                                </button>
                            </div>
                        )}
                    </form>
                </div>

                <div className="modal-footer-illustration">
                    <div className="earth-hands-img"></div>
                </div>
            </div>
        </div>
    );
};

export default PledgeModal;
